import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import BackButton from '@/components/BackButton';
import Button from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import {
  PageHeading,
  SectionHeading,
  UserRoleHeading,
} from '@/components/Layout/Parts';
import { TableHeader } from '@/components/Table';
import Text from '@/components/Text';
import useDocumentSignedNotifications from '@/hooks/payments/useDocumentSignedNotifications';
import useVacationRequestedNotifications from '@/hooks/payments/useVacationRequestedNotifications';
import useAlerts from '@/hooks/useAlerts';
import useUser from '@/hooks/useUser';
import { api } from '@/utils/api';

import { NotifType } from '../FreelancersList/components/FreelancersNotifications';
import Notification from '../FreelancersList/components/Notification';

const NotifsHolder = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.colors.grayscale};
`;

const Placeholder = styled.div`
  padding: 40px 12px;
  text-align: center;
  background-color: ${({ theme }) => theme.colors.white};
  border: solid 1px ${({ theme }) => theme.colors.grayscale};
`;

const NotifsCount = styled.div<{ $zero?: boolean }>`
  display: inline-flex;
  width: 26px;
  height: 18px;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.success};
  border-radius: 9px;
  ${({ $zero, theme }) =>
    $zero &&
    css`
      background-color: ${theme.colors.gray};
    `}
`;

const StyledSectionHeading = styled(SectionHeading)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FreelancersNotificationCenter = () => {
  const [activeTab, setActiveTab] = useState<NotifType>(NotifType.Documents);

  const user = useUser();
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const { notifications: docSignedNotifications } =
    useDocumentSignedNotifications(user?.id, true, false);
  const { notifications: vacationNotifications } =
    useVacationRequestedNotifications(user?.id, true, false);

  const activeNotifications =
    activeTab === NotifType.Documents
      ? docSignedNotifications
      : vacationNotifications;

  const unreadNotifications = activeNotifications?.filter(
    (n) => n.readAt === null,
  );

  const handleMarkAllAsRead = async () => {
    if (!unreadNotifications?.length) return;

    try {
      const notificationType =
        activeTab === NotifType.Documents
          ? 'App\\Notifications\\DocumentSigned'
          : 'App\\Notifications\\FreelancerHolidayRequested';

      await api.post('/v1/notifications/mark-read', {
        types: [notificationType],
      });

      const queryKey =
        activeTab === NotifType.Documents
          ? 'document_signed'
          : 'vacation_requested';

      await queryClient.invalidateQueries([queryKey], {
        exact: false,
      });
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <>
      <UserRoleHeading />
      <PageHeading>Notification Center</PageHeading>
      <BackButton />
      <TableHeader>
        <Controls>
          <ControlsButton
            active={activeTab === NotifType.Documents}
            onClick={() => setActiveTab(NotifType.Documents)}
          >
            DocuSign
          </ControlsButton>
          <ControlsButton
            active={activeTab === NotifType.Vacation}
            onClick={() => setActiveTab(NotifType.Vacation)}
          >
            Vacation Requests
          </ControlsButton>
        </Controls>
        <Button
          small
          color="secondary"
          disabled={!unreadNotifications?.length}
          onClick={handleMarkAllAsRead}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M13.5 4.50049L6.5 11.5002L3 8.00049"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Mark as Read
        </Button>
      </TableHeader>
      <StyledSectionHeading>
        notifications list
        {unreadNotifications?.length ? (
          <NotifsCount>
            <Text $color="white" $type="extraSmall">
              {unreadNotifications.length}
            </Text>
          </NotifsCount>
        ) : (
          <NotifsCount $zero>
            <Text $color="white" $type="extraSmall">
              0
            </Text>
          </NotifsCount>
        )}
      </StyledSectionHeading>
      {activeNotifications?.length ? (
        <NotifsHolder key={activeTab}>
          {activeNotifications.map((notification) => (
            <Notification
              key={notification.id}
              notification={notification}
              inCenter
              type={activeTab}
            />
          ))}
        </NotifsHolder>
      ) : (
        <Placeholder key={activeTab}>
          <Text $color="black" $type="caption">
            No notifications at this time
          </Text>
        </Placeholder>
      )}
    </>
  );
};

export default FreelancersNotificationCenter;
